// Engine ScanController that drives the in-scene camera scanner.
//
// Injected into the production CameraSceneScannerAdapter by Stage. It keeps the
// sticker boundary decoupled from the scene: the adapter depends only on the
// ScanController interface, this controller depends only on injected activate/
// deactivate callbacks (which Stage wires to scene_service.ui_message) and the
// shared ScanBridge. Neither imports the Video/capture/GPU code directly.

import type { ScanController } from '../sticker/internal/scan_controller';
import type { AdapterScanRequest } from '../sticker/sticker_adapter';
import { StickerError, type StickerCode } from '../sticker/sticker_types';
import { scanBridge, type ScanBridge } from './scan_bridge';

export interface SceneScanControllerOptions {
  /** Bring the in-scene Video scanner up (Stage wires to a scene ui_message). */
  activate: () => void;
  /** Tear the in-scene Video scanner down. */
  deactivate: () => void;
  /** Whether a usable camera exists. Defaults to a navigator/getUserMedia probe. */
  cameraSupported?: () => boolean;
  /** Injectable bridge for tests; defaults to the engine singleton. */
  bridge?: ScanBridge;
}

function defaultCameraSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

export class SceneScanController implements ScanController {
  private readonly activate: () => void;
  private readonly deactivate: () => void;
  private readonly cameraSupported: () => boolean;
  private readonly bridge: ScanBridge;

  constructor(options: SceneScanControllerOptions) {
    this.activate = options.activate;
    this.deactivate = options.deactivate;
    this.cameraSupported = options.cameraSupported ?? defaultCameraSupported;
    this.bridge = options.bridge ?? scanBridge;
  }

  async scan(request: AdapterScanRequest): Promise<StickerCode> {
    if (!this.cameraSupported()) {
      throw new StickerError(
        'unsupported',
        'No camera is available for scanning on this device.'
      );
    }
    return this.bridge.begin_scan(request.signal, this.activate, this.deactivate);
  }
}
